import { useCallback, useReducer } from 'react';
import omit from 'lodash/omit';
import { Node } from 'prosemirror-model';

import { useTranslation } from '@lyearn/i18n';
import { EditorView, useRequestContext } from '@lyearn/lygo-core';
import { useSnackbar } from '@lyearn/ui';

import { setNodeAttr } from '../../../../utils';
import { getCloudflareStreamingIdFromVideoSrc } from '../../plugins/utils';
import { CaptionMap, ReducerAction, SubtitleActionTypes, SubtitleState } from './types';

const initialState = (captions: CaptionMap, captionDisabled: string[]) => {
  return {
    captions: captions,
    localFile: null,
    disabedCaptions: captionDisabled,
    loadingCaption: '',
  };
};

const reducer = (state: SubtitleState, action: ReducerAction) => {
  switch (action.type) {
    case SubtitleActionTypes.Caption:
      return {
        ...state,
        captions: action.payload.captions!,
      };
    case SubtitleActionTypes.DisabedCaptions:
      return {
        ...state,
        disabedCaptions: action.payload.disabedCaptions!,
      };
    case SubtitleActionTypes.File:
      return {
        ...state,
        localFile: action.payload.localFile!,
      };
    case SubtitleActionTypes.LoadingCaption:
      return {
        ...state,
        loadingCaption: action.payload.loadingCaption!,
      };
    default:
      return state;
  }
};

export const useSubtitle = (view: EditorView, currentSelectionNode: Node, pos: number) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialState(
      currentSelectionNode.attrs.captions || {},
      currentSelectionNode.attrs.disabedCaptions || [],
    ),
  );
  const { postRequest } = useRequestContext();
  const { t } = useTranslation();
  const { showErrorSnackbar, showSnackbar } = useSnackbar();

  const onCaptionSubmit = useCallback(
    (language: string) => {
      if (!state.localFile) {
        return;
      }
      const formData = new FormData();
      formData.append('subtitleFile', state.localFile, state.localFile.name);
      formData.append('language', language);
      formData.append(
        'videoId',
        getCloudflareStreamingIdFromVideoSrc(currentSelectionNode.attrs.src),
      );
      dispatch({ type: SubtitleActionTypes.LoadingCaption, payload: { loadingCaption: language } });

      postRequest?.(`/external/uploadCaption`, {}, { body: formData, headers: null }).then(
        (res: any) => {
          if (res.success) {
            const newState = { ...state.captions };
            newState[language] = res.url;
            dispatch({ type: SubtitleActionTypes.LoadingCaption, payload: { loadingCaption: '' } });
            dispatch({ type: SubtitleActionTypes.Caption, payload: { captions: newState } });
            setNodeAttr(view, pos, 'captions', newState);
            dispatch({ type: SubtitleActionTypes.File, payload: { localFile: null } });
            showSnackbar({
              message: t('Lygo:AdvancedMenu.video.Subtitles.uploadSubtitleSuccess'),
            });
          } else {
            dispatch({ type: SubtitleActionTypes.LoadingCaption, payload: { loadingCaption: '' } });
            showErrorSnackbar({
              message: t('Lygo:AdvancedMenu.video.Subtitles.uploadSubtitleError'),
            });
          }
        },
      );
    },
    [
      currentSelectionNode.attrs.src,
      pos,
      postRequest,
      showErrorSnackbar,
      showSnackbar,
      state.captions,
      state.localFile,
      t,
      view,
    ],
  );

  const onToggleCaption = useCallback(
    (language: string) => {
      const newState = [...state.disabedCaptions];
      if (newState.includes(language)) {
        newState.splice(newState.indexOf(language), 1);
      } else {
        newState.push(language);
      }
      dispatch({
        type: SubtitleActionTypes.DisabedCaptions,
        payload: { disabedCaptions: newState },
      });
      setNodeAttr(view, pos, 'disabedCaptions', newState);
    },
    [state.disabedCaptions, view, pos],
  );

  const onDeleteCaption = useCallback(
    (language: string) => {
      const newState = omit({ ...state.captions }, language);
      dispatch({ type: SubtitleActionTypes.Caption, payload: { captions: newState } });
      setNodeAttr(view, pos, 'captions', newState);
    },
    [pos, state.captions, view],
  );

  return {
    onDeleteCaption,
    onToggleCaption,
    onCaptionSubmit,
    state,
    dispatch,
    t,
  };
};
